// /admin/planner — the days ahead: genre/kit, key, instruments (with alternates), effects and
// status (who writes for it, frozen). Plan further ahead, and edit any future day nobody writes for
// yet; "Try in Sound Lab" auditions a setup before saving it.
import { useCallback, useEffect, useState } from "react";
import {
  DELAY_TICKS,
  getPreset,
  periodAlternates,
  keyName,
  KITS,
  NOTE_NAMES,
  type InstrumentRole,
  type KitId,
  type PeriodDef,
} from "@eb/shared";
import { api, ApiError } from "../../api";
import { Link, useTitle } from "../../lib/router";
import { Modal } from "../../ui/Modal";
import { AdminShell } from "./AdminShell";
import { PresetSelect } from "./PresetSelect";
import { defaultAlternates, INSTRUMENT_ROLES, KIT_OPTIONS, ROLE_LABEL } from "./setup";
import { encodeSetup, type LabSetup } from "./labSetup";

interface PeriodRow {
  day: number;
  date: string;
  def: PeriodDef;
  overridden: boolean;
  songs: number;
  drafts: number;
  frozen: boolean;
  lock: string | null;
}

interface PlannerData {
  today: number;
  lastPlanned: number | null;
  maxPlanDays: number;
  periods: PeriodRow[];
}

export const DELAY_LABEL: Record<number, string> = { 480: "1/8", 720: "1/8 dotted", 960: "1/4", 1440: "1/4 dotted" };

const weekday = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });

export function setupOf(def: PeriodDef): LabSetup {
  return {
    kit: def.kit,
    key: def.key,
    instruments: def.instruments,
    alternates: periodAlternates(def),
    fx: def.fx,
    seed: def.seed,
  };
}

export function Planner() {
  useTitle("Planner");
  const [range, setRange] = useState(30);
  const [data, setData] = useState<PlannerData | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<PeriodRow | null>(null);
  const [planUntil, setPlanUntil] = useState(60);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api<PlannerData>("GET", `/api/admin/periods?ahead=${range}`)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [range]);
  useEffect(load, [load]);

  const plan = async () => {
    if (!data) return;
    setMsg("");
    try {
      const r = await api<{ created: number; until: number }>("POST", "/api/admin/periods/plan", {
        until: data.today + planUntil,
      });
      setMsg(`Planned ${r.created} new day${r.created === 1 ? "" : "s"} (up to day ${r.until}).`);
      setRange(Math.max(range, planUntil));
      load();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <AdminShell>
      <h1>Planner</h1>
      <p className="muted">
        Every day has a key, a drum kit (its genre) and three instruments with two alternates each. Days are planned
        automatically 14 days ahead; you can plan further and change any future day nobody writes for yet.
      </p>
      <section className="panel row planner-tools" aria-label="Planning">
        <label className="field">
          <span>Show</span>
          <select value={range} onChange={(e) => setRange(Number(e.target.value))} data-testid="planner-range">
            {[14, 30, 60, 120, 365].map((n) => (
              <option key={n} value={n}>
                next {n} days
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Plan ahead to</span>
          <select value={planUntil} onChange={(e) => setPlanUntil(Number(e.target.value))} data-testid="plan-until">
            {[30, 60, 90, 180, 365].map((n) => (
              <option key={n} value={n}>
                {n} days
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn" onClick={() => void plan()} data-testid="plan-ahead">
          Plan
        </button>
        {data?.lastPlanned !== null && data && (
          <span className="muted small">
            Planned up to day {data.lastPlanned} (today is day {data.today}).
          </span>
        )}
        {msg && (
          <span role="status" className="small">
            {msg}
          </span>
        )}
      </section>
      {error && <p role="alert">{error}</p>}
      {!data ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="table-scroll">
          <table className="planner-table" data-testid="planner-table">
            <thead>
              <tr>
                <th scope="col">Day</th>
                <th scope="col">Genre · kit</th>
                <th scope="col">Key</th>
                <th scope="col">Bass</th>
                <th scope="col">Chords</th>
                <th scope="col">Lead</th>
                <th scope="col">FX</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.periods.map((p) => {
                const alts = p.def.alternates;
                const isToday = p.day === data.today;
                return (
                  <tr key={p.day} className={isToday ? "today-row" : ""} data-day={p.day}>
                    <td>
                      <strong>{p.date}</strong>
                      <span className="muted small">
                        {" "}
                        {weekday(p.date)} · #{p.day}
                      </span>
                    </td>
                    <td>
                      <strong>{KITS[p.def.kit].genre}</strong>
                      <span className="muted small"> {KITS[p.def.kit].name}</span>
                    </td>
                    <td>{keyName(p.def.key)}</td>
                    {INSTRUMENT_ROLES.map((r) => (
                      <td key={r}>
                        {getPreset(p.def.instruments[r]).name}
                        {alts && (
                          <span className="muted small planner-alts">
                            {alts[r].map((id) => getPreset(id).name).join(" · ")}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="small">
                      rev {Math.round(p.def.fx.reverbSize * 100)}%<br />
                      dly {DELAY_LABEL[p.def.fx.delayTicks] ?? p.def.fx.delayTicks}
                    </td>
                    <td>
                      <span className="planner-status">
                        {isToday && <span className="chip chip-accent">Today</span>}
                        {p.frozen && <span className="chip">Frozen</span>}
                        {p.songs > 0 && (
                          <span className="chip">
                            {p.songs} song{p.songs === 1 ? "" : "s"}
                          </span>
                        )}
                        {p.drafts > 0 && (
                          <span className="chip">
                            {p.drafts} draft{p.drafts === 1 ? "" : "s"}
                          </span>
                        )}
                        {p.overridden && <span className="chip">Edited</span>}
                        {!p.lock && <span className="chip chip-ok">Editable</span>}
                      </span>
                    </td>
                    <td>
                      <span className="row planner-actions">
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={!!p.lock}
                          title={p.lock ?? "Change this day"}
                          onClick={() => setEditing(p)}
                          data-testid={`edit-${p.day}`}
                        >
                          Edit
                        </button>
                        <Link className="btn btn-small btn-ghost" href={`/admin/lab?day=${p.day}`}>
                          Sound Lab
                        </Link>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <DayEditor
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </AdminShell>
  );
}

function DayEditor({ row, onClose, onSaved }: { row: PeriodRow; onClose: () => void; onSaved: () => void }) {
  const [s, setS] = useState<LabSetup>(() => setupOf(row.def));
  const [reseed, setReseed] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const setMain = (r: InstrumentRole, id: string) =>
    setS({
      ...s,
      instruments: { ...s.instruments, [r]: id },
      alternates: { ...s.alternates, [r]: defaultAlternates(id, s.seed) },
    });
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api("PUT", `/api/admin/periods/${row.day}`, {
        kit: s.kit,
        key: s.key,
        instruments: s.instruments,
        alternates: s.alternates,
        fx: s.fx,
        reseed,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Day ${row.day} — ${row.date}`} onClose={onClose}>
      <div className="stack day-editor" data-testid="day-editor">
        <div className="admin-grid">
          <label className="stack-label">
            <span>Drum kit (genre)</span>
            <select value={s.kit} onChange={(e) => setS({ ...s, kit: e.target.value as KitId })} data-testid="day-kit">
              {KIT_OPTIONS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
          <label className="stack-label">
            <span>Key</span>
            <span className="row">
              <select
                value={s.key.tonic}
                onChange={(e) => setS({ ...s, key: { ...s.key, tonic: Number(e.target.value) } })}
                aria-label="Tonic"
              >
                {NOTE_NAMES.map((n, i) => (
                  <option key={n} value={i}>
                    {n}
                  </option>
                ))}
              </select>
              <select
                value={s.key.mode}
                onChange={(e) => setS({ ...s, key: { ...s.key, mode: e.target.value as "major" | "minor" } })}
                aria-label="Mode"
              >
                <option value="major">major</option>
                <option value="minor">minor</option>
              </select>
            </span>
          </label>
          <label className="stack-label">
            <span>Reverb size {Math.round(s.fx.reverbSize * 100)}%</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={s.fx.reverbSize}
              onChange={(e) => setS({ ...s, fx: { ...s.fx, reverbSize: Number(e.target.value) } })}
            />
          </label>
          <label className="stack-label">
            <span>Delay time</span>
            <select
              value={s.fx.delayTicks}
              onChange={(e) => setS({ ...s, fx: { ...s.fx, delayTicks: Number(e.target.value) } })}
            >
              {DELAY_TICKS.map((d) => (
                <option key={d} value={d}>
                  {DELAY_LABEL[d]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {INSTRUMENT_ROLES.map((r) => (
          <fieldset key={r} className="role-fieldset">
            <legend>{ROLE_LABEL[r]}</legend>
            <div className="admin-grid">
              <label className="stack-label">
                <span>Main</span>
                <PresetSelect
                  role={r}
                  value={s.instruments[r]}
                  onChange={(id) => setMain(r, id)}
                  label={`${ROLE_LABEL[r]} main`}
                  testId={`day-${r}`}
                />
              </label>
              {[0, 1].map((i) => (
                <label key={i} className="stack-label">
                  <span>Alternate {i + 1}</span>
                  <PresetSelect
                    role={r}
                    value={s.alternates[r][i]}
                    onChange={(id) => {
                      const pair = [...s.alternates[r]] as [string, string];
                      pair[i] = id;
                      setS({ ...s, alternates: { ...s.alternates, [r]: pair } });
                    }}
                    label={`${ROLE_LABEL[r]} alternate ${i + 1}`}
                  />
                </label>
              ))}
            </div>
          </fieldset>
        ))}
        <label className="toggle">
          <input type="checkbox" checked={reseed} onChange={(e) => setReseed(e.target.checked)} />
          <span>New drum break and dawn variations</span>
        </label>
        <p className="muted small">
          ★ = can be a day's main sound. Alternates are offered to writers next to the main sound; picking a new main
          suggests two of its family.
        </p>
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <div className="row">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
            data-testid="day-save"
          >
            {saving ? "Saving…" : "Save day"}
          </button>
          <Link className="btn" href={`/admin/lab?setup=${encodeSetup(s)}`} data-testid="day-try">
            Try in Sound Lab
          </Link>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
